import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, XCircle, AlertTriangle, MapPin, Mail } from "lucide-react";
import { ROUTES_CONFIG } from "src/constants/routesConfig";

interface ModalProps {
  purchaseOrderId: string;
  onClose?: () => void;
}

interface SuccessProps extends ModalProps {
  isCollectionOrder?: boolean;
}

interface WarningProps extends ModalProps {
  onCancel: () => void;
  onConfirm: () => void;
}

interface ErrorProps extends ModalProps {
  onRetry: () => void;
}

const Success: React.FC<SuccessProps> = ({ purchaseOrderId, onClose, isCollectionOrder }) => {
  if (purchaseOrderId.startsWith("AA-") && !isCollectionOrder) {
    return <ActiveAntsSuccess purchaseOrderId={purchaseOrderId} onClose={onClose} />;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <CheckCircle2 className="w-6 h-6 text-success-600" />
            <h2 className="text-lg font-semibold text-gray-900">Purchase Order Updated</h2>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
              &times;
            </button>
          )}
        </div>

        <div className="p-5 space-y-4">
          <div className="bg-success-50 border border-success-200 rounded-lg p-4">
            <p className="text-sm text-gray-700">
              The PO confirmation details have been successfully updated{" "}
              <span className="font-semibold text-success-700">(ID: {purchaseOrderId})</span>
            </p>
          </div>
          <div className="text-center space-y-2">
            <p className="text-sm text-gray-600">You'll receive a follow up email shortly</p>
            <p className="text-sm text-gray-600">We look forward to future collaboration</p>
          </div>

          {/* Home Button */}
          <div className="flex justify-center pt-2">
            <Link to={ROUTES_CONFIG.home.path}>
              <button className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium">
                Home
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const ActiveAntsSuccess: React.FC<SuccessProps> = ({ purchaseOrderId, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full overflow-hidden my-8">
        <div className="bg-gradient-to-r from-warning-600 to-warning-700 px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <CheckCircle2 className="w-7 h-7 text-white" />
              <div>
                <h2 className="text-xl font-bold text-white">PO Confirmed — Important Notice</h2>
                <p className="text-sm text-warning-100 mt-0.5">This order ships directly to Active Ants (EU 3PL)</p>
              </div>
            </div>
            {onClose && (
              <button onClick={onClose} className="text-white/80 hover:text-white text-2xl leading-none">
                &times;
              </button>
            )}
          </div>
        </div>

        <div className="p-6 space-y-5">
          <div className="bg-success-50 border border-success-200 rounded-lg p-4">
            <p className="text-sm text-gray-700">
              Thanks — your confirmation for <span className="font-semibold text-success-700">{purchaseOrderId}</span>{" "}
              has been received.
            </p>
          </div>

          <div className="border-l-4 border-warning-500 bg-warning-50 rounded-r-lg p-4">
            <p className="text-sm font-semibold text-warning-900 mb-2">⚠ This shipment must go to Active Ants.</p>
            <p className="text-sm text-warning-800">
              The goods must be shipped directly to the Active Ants warehouse in the Netherlands.
            </p>
          </div>

          <div className="border border-gray-200 rounded-lg p-4 space-y-3">
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Delivery Address</p>
                <p className="text-sm text-gray-900 font-medium leading-relaxed">
                  Healf c/o Active Ants
                  <br />
                  Borchwerf 5
                  <br />
                  4704 RG Roosendaal
                  <br />
                  Netherlands
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 pt-3 border-t border-gray-100">
              <Mail className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Book your inbound slot</p>
                <p className="text-sm text-gray-900">
                  Contact Active Ants at{" "}
                  <a
                    href="mailto:happytohelp@activeants.nl"
                    className="font-medium text-primary-700 hover:text-primary-900 underline"
                  >
                    happytohelp@activeants.nl
                  </a>{" "}
                  to book a delivery slot.
                </p>
                <p className="text-xs text-gray-600 mt-2">
                  Quote the client name{" "}
                  <span className="font-semibold text-gray-900">Healf / The Healthy Living Store Limited</span> in all
                  communications so AA can identify the inbound.
                </p>
              </div>
            </div>
          </div>

          <p className="text-xs text-gray-500 text-center">
            A copy of these instructions has also been emailed to you. We look forward to future collaboration.
          </p>

          <div className="flex justify-center pt-2">
            <Link to={ROUTES_CONFIG.home.path}>
              <button className="px-6 py-2.5 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium">
                Done
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const CancelSuccess: React.FC<SuccessProps> = ({ purchaseOrderId, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <CheckCircle2 className="w-6 h-6 text-success-600" />
            <h2 className="text-lg font-semibold text-gray-900">Purchase Order Cancelled</h2>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
              &times;
            </button>
          )}
        </div>

        <div className="p-5 space-y-4">
          <div className="bg-success-50 border border-success-200 rounded-lg p-4">
            <p className="text-sm text-gray-700">
              The purchase order has been cancelled{" "}
              <span className="font-semibold text-success-700">(ID: {purchaseOrderId})</span>
            </p>
          </div>
          <p className="text-sm text-gray-600 text-center">We look forward to future collaboration</p>

          {/* Home Button */}
          <div className="flex justify-center pt-2">
            <Link to={ROUTES_CONFIG.home.path}>
              <button className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium">
                Home
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const CancelWarning: React.FC<WarningProps> = ({ purchaseOrderId, onConfirm, onCancel }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="w-6 h-6 text-warning-600" />
            <h2 className="text-lg font-semibold text-gray-900">Cancel Purchase Order</h2>
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
            &times;
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertTriangle className="w-5 h-5 text-warning-600 mt-0.5 mr-2 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-warning-800 mb-1">
                  Are you sure you want to cancel this purchase order?
                </p>
                <p className="text-sm text-warning-700">ID: {purchaseOrderId}</p>
              </div>
            </div>
          </div>
          <p className="text-sm text-gray-700 text-center font-medium">This action cannot be undone</p>
        </div>

        {/* Footer Buttons */}
        <div className="flex justify-end space-x-3 p-5 border-t border-gray-200 bg-gray-50">
          <button
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm font-medium"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 bg-error-600 text-white rounded-md hover:bg-error-700 transition-colors text-sm font-medium"
            onClick={onConfirm}
          >
            Confirm Cancellation
          </button>
        </div>
      </div>
    </div>
  );
};

const Error: React.FC<ErrorProps> = ({ onRetry, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <XCircle className="w-6 h-6 text-error-600" />
            <h2 className="text-lg font-semibold text-gray-900">Update Failed</h2>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
              &times;
            </button>
          )}
        </div>

        <div className="p-5">
          <p className="text-sm text-gray-700 text-center mb-6">
            An error occurred while updating the purchase order. Please try again.
          </p>

          {/* Footer Button */}
          <div className="flex justify-center">
            <button
              className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium"
              onClick={onRetry}
            >
              Retry Update
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ScheduledPO: React.FC<WarningProps> = ({ purchaseOrderId, onConfirm, onCancel }) => {
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden border border-secondary-200">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-800 to-primary-900 px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Backorder Will Be Created</h2>
                <p className="text-sm text-primary-100 mt-0.5">Low Confirmation Rate Detected</p>
              </div>
            </div>
            <button
              onClick={onCancel}
              className="text-white/80 hover:text-white text-2xl leading-none transition-colors w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10"
            >
              &times;
            </button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Info Alert */}
          <div className="bg-primary-50 border-l-4 border-primary-800 rounded-lg p-4">
            <div className="flex items-start">
              <AlertTriangle className="w-5 h-5 text-primary-800 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-primary-900 mb-1">
                  Confirmation rate is below 50% for some products
                </p>
                <p className="text-sm text-primary-800">
                  A backorder purchase order(s) with missing quantities will be automatically created based on the
                  restock dates you provided for each product.
                </p>
                <p className="text-xs text-primary-700 mt-2 font-medium">PO ID: {purchaseOrderId}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="flex justify-end space-x-3 p-6 border-t border-secondary-200 bg-secondary-50">
          <button
            className="px-5 py-2.5 bg-secondary-200 text-secondary-800 rounded-lg hover:bg-secondary-300 transition-colors text-sm font-medium"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className="px-5 py-2.5 bg-primary-700 text-white rounded-lg hover:bg-primary-800 transition-colors text-sm font-medium shadow-sm"
            onClick={onConfirm}
          >
            Confirm & Proceed
          </button>
        </div>
      </div>
    </div>
  );
};

const UpdatePurchaseOrderConfirmationModals = {
  Success,
  CancelSuccess,
  CancelWarning,
  ScheduledPO,
  Error
};

export { UpdatePurchaseOrderConfirmationModals };
export { BackorderPOModal } from "./backorderPOModal";
